import { useState } from 'react';
import { Calendar } from '@belong/ui';
import { formatString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getImprovementsSpecialist } from '../utils';

interface StepResource {
  homeListingDetailsModel: {
    owner: {
      firstName: string;
    };
    basicInfo: {
      timeZone?: string;
    };
    employeeAssignments: {
      [key: string]: {
        employeeAssignment: {
          assignmentType: string;
        };
        employee: {
          bannerImageUrl: string;
        };
        user: {
          profileImageUrl: string;
        };
      };
    };
  };
}

interface Props {
  stepResource: StepResource;
  onSaveAndNext: (data: {
    estimatedHomeownerFixesCompletionDate: string;
  }) => void;
}

const HomeownerFixes: React.FC<Props> = ({ stepResource, onSaveAndNext }) => {
  // `useState(null)` typed the state as `null`, so `completionDate` could never
  // hold the date the calendar produces. `undefined` rather than `null` to match
  // `Calendar`'s `onSelect`, which hands back `Date | undefined`; both are
  // equally falsy for the `disabled` check and for `DayPicker`'s `selected`.
  const [completionDate, setCompletionDate] = useState<Date | undefined>();
  const { homeListingDetailsModel } = stepResource;

  const formattedAssessedBy = getImprovementsSpecialist(
    homeListingDetailsModel,
  );

  const handleSubmit = () => {
    onSaveAndNext({
      // The CTA is `disabled={!completionDate}`, so this only runs with a date;
      // and splitting an ISO string on 'T' always yields a first element.
      estimatedHomeownerFixesCompletionDate: completionDate!
        .toISOString()
        .split('T')[0]!,
    });
  };

  return (
    <PostInspectionContentLayout
      title={formatString(
        POST_INSPECTION_FLOW_STRINGS['homeowner-fixes.title'],
        {
          firstName: homeListingDetailsModel.owner.firstName,
        },
      )}
      employee={formattedAssessedBy}
    >
      <Calendar
        rootClassName="m-0 w-full md:p-0"
        selected={completionDate}
        disabledDays={[
          { before: new Date(new Date().getTime() + 24 * 60 * 60 * 1000) },
        ]}
        onSelect={(date) => {
          setCompletionDate(date);
        }}
      />

      <div className="my-2xl flex justify-center">
        <PostInspectionContentLayout.CtaButton
          onClick={() => handleSubmit()}
          disabled={!completionDate}
        >
          Save
        </PostInspectionContentLayout.CtaButton>
      </div>
    </PostInspectionContentLayout>
  );
};

export default HomeownerFixes;
